import { ApprovalOutlined, BlockOutlined, PendingOutlined } from '@mui/icons-material'
import {
  Button,
  Chip,
  Link,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material'
import { ReactElement, useMemo, useState } from 'react'
import { useUser } from 'src/hooks'
import type { OemRequestRow, OemRequestStatus } from './SupplierList.types'
import './Supplier.css'

const COLUMN_WIDTHS = {
  supplierName: undefined,
  requestedOem: '30%',
  loa: '12%',
  status: '20%',
  save: '12%',
}

const STATUS_DISPLAY: Record<
  OemRequestStatus,
  { label: string; color: 'default' | 'success' | 'error'; icon: ReactElement }
> = {
  pending: { label: 'Pending', color: 'default', icon: <PendingOutlined /> },
  approved: { label: 'Approved', color: 'success', icon: <ApprovalOutlined /> },
  rejected: { label: 'Rejected', color: 'error', icon: <BlockOutlined /> },
}

export function StatusChip({ status }: { status: OemRequestStatus }) {
  const { label, color, icon } = STATUS_DISPLAY[status]
  return <Chip size="small" label={label} color={color} icon={icon} />
}

export interface OemRequestTableProps {
  rows: OemRequestRow[]
  loading: boolean
  pageSize: number
  onSaveStatus: (row: OemRequestRow, status: OemRequestStatus) => void
}

export function OemRequestTable({ rows, loading, pageSize, onSaveStatus }: OemRequestTableProps) {
  const user = useUser()
  const canApprove = useMemo(() => user.role.admin || user.role.operations, [user.role])
  const [draftStatuses, setDraftStatuses] = useState<Partial<Record<string, OemRequestStatus>>>({})

  return (
    <Table stickyHeader className="supplier-table">
      <TableHead>
        <TableRow>
          <TableCell width={COLUMN_WIDTHS.supplierName}>Supplier Name</TableCell>
          <TableCell width={COLUMN_WIDTHS.requestedOem}>Requested OEM</TableCell>
          <TableCell width={COLUMN_WIDTHS.loa}>Uploaded LOA</TableCell>
          <TableCell width={COLUMN_WIDTHS.status}>Status</TableCell>
          {canApprove && <TableCell width={COLUMN_WIDTHS.save} align="right"></TableCell>}
        </TableRow>
      </TableHead>

      <TableBody>
        {loading
          ? Array.from({ length: pageSize }).map((_, index) => (
              <TableRow key={index}>
                {Array.from({ length: Object.keys(COLUMN_WIDTHS).length - (canApprove ? 0 : 1) }).map((_, index) => (
                  <TableCell key={index}>
                    <Skeleton />
                  </TableCell>
                ))}
              </TableRow>
            ))
          : rows.map(row => {
              const draftStatus = draftStatuses[row.id] ?? row.status
              return (
                <TableRow key={row.id} hover>
                  <TableCell>{row.supplierName}</TableCell>
                  <TableCell>{row.provider}</TableCell>
                  <TableCell>
                    {/*TODO: link to the request's LOA*/}
                    <Link component="button" variant="body2" underline="hover" disabled>
                      Link
                    </Link>
                  </TableCell>
                  <TableCell>
                    {canApprove ? (
                      <Select
                        size="small"
                        variant="standard"
                        disableUnderline
                        value={draftStatus}
                        renderValue={value => <StatusChip status={value} />}
                        onChange={e =>
                          setDraftStatuses(prev => ({ ...prev, [row.id]: e.target.value as OemRequestStatus }))
                        }
                      >
                        <MenuItem value="pending" className="oem-request-hidden-option">
                          Pending
                        </MenuItem>
                        <MenuItem value="approved">Approved</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                      </Select>
                    ) : (
                      <StatusChip status={row.status} />
                    )}
                  </TableCell>

                  {canApprove && (
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={draftStatus === row.status}
                        onClick={() => onSaveStatus(row, draftStatus)}
                      >
                        SAVE
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              )
            })}
      </TableBody>
    </Table>
  )
}
